/**
 * Glue between persisted WidgetConfig and the runtime aggregation adapter:
 * resolves a dataset's fields (via the enhanced /fields endpoint) and turns a
 * WidgetConfig + dashboard-level filters into a `ResolvedWidget`.
 */

import {
  resolveFieldsFromEnhancedEndpoint,
  type EnhancedFieldsResponse,
} from '../field-filter-pipeline.ts';
import type { FilterClauseContext } from '../filter-clause-builder.ts';
import { buildConfiguredFilterClauses, buildExtraFilterClauses } from '../filter-query-builder.ts';
import type { FieldOption } from '../opensearch-field-mapper.ts';
import {
  getModelTypeFromDataset,
  getCompanyFilterField,
  getSearchRequestModelType,
} from '../dataset-utils.ts';
import { AGG_LABELS } from '../aggregation-types.ts';
import { fieldsRequestFor } from '../search-requests.ts';
import type { ResolvedMeasure, ResolvedWidget } from './widget-data-adapter.ts';
import type {
  MeasureSpec,
  WidgetConfig,
  BuildFilterConfig,
  UserFilterConfig,
} from './widget-config-types.ts';
import { effectiveWidgetFilters } from './effective-widget-filters.ts';
import type { Drilldown } from './drilldown.ts';
import type { Store as StoreService } from '@warp-drive/core';

/** Milliseconds a dataset's field registry is reused after its request succeeds. */
export const DATASET_FIELDS_REUSE_MS = 60_000;

type FieldsEntry = { response: Promise<EnhancedFieldsResponse>; expiresAt: number };

const fieldsByStore = new WeakMap<StoreService, Map<string, FieldsEntry>>();

function datasetFieldsResponse(store: StoreService, modelType: string, companyId?: string) {
  let entries = fieldsByStore.get(store);
  if (!entries) {
    entries = new Map();
    fieldsByStore.set(store, entries);
  }
  const key = `${modelType}\u0000${companyId ?? ''}`;
  const cached = entries.get(key);
  if (cached && cached.expiresAt > Date.now()) return cached.response;

  // Source-kind seam (RFC 062): OpenSearch datasets hit the classic /fields
  // endpoint, SQL datasets sql-search-fields — same enhanced response shape.
  const response = Promise.resolve(
    store.request<EnhancedFieldsResponse>(fieldsRequestFor(modelType, companyId))
  ).then(({ content }) => {
    entry.expiresAt = Date.now() + DATASET_FIELDS_REUSE_MS;
    return content;
  });
  const entry: FieldsEntry = { response, expiresAt: Infinity };
  entries.set(key, entry);
  response.catch(() => {
    if (entries.get(key) === entry) entries.delete(key);
  });
  return response;
}

/**
 * Fetch and map the field registry for a dataset (optionally company-scoped). Calls on one
 * store share a request per dataset and company until `DATASET_FIELDS_REUSE_MS` lapses; failures aren't kept.
 */
export async function resolveDatasetFields(
  store: StoreService,
  dataset: string,
  companyId?: string,
  showIndividualApplicationsOnly = true
): Promise<FieldOption[]> {
  const modelType = getSearchRequestModelType(
    getModelTypeFromDataset(dataset),
    showIndividualApplicationsOnly
  );
  return resolveFieldsFromEnhancedEndpoint(
    await datasetFieldsResponse(store, modelType, companyId)
  );
}

/** The shared merge, run through the report-builder clause assembler. */
function widgetFilterClauses(
  widget: WidgetConfig,
  dashboardBuild: BuildFilterConfig | undefined,
  dashboardUser: UserFilterConfig | undefined,
  drilldowns: Drilldown[] | undefined,
  ctx: FilterClauseContext
) {
  const { buildFilterValues, buildFilterLayout, userFilterValues, extraFilterValues } =
    effectiveWidgetFilters(widget, {
      buildFilters: dashboardBuild,
      userFilters: dashboardUser,
      drilldowns,
    });
  const configured = buildConfiguredFilterClauses(
    { buildFilterValues, buildFilterLayout, userFilterValues },
    ctx
  );
  // Appended as separate clauses, never merged into the user map, so a same-key entry ANDs.
  return [...configured, ...buildExtraFilterClauses(extraFilterValues, ctx)];
}

/** Human-readable legend label for a measure. */
function measureLabel(spec: MeasureSpec, field: FieldOption | null): string {
  if (spec.label) return spec.label;
  if (!field) return 'Count';
  return spec.aggregation === 'count'
    ? field.label
    : `${AGG_LABELS[spec.aggregation]} ${field.label}`;
}

export interface ToResolvedWidgetOptions {
  companyId?: string;
  buildFilters?: BuildFilterConfig;
  userFilters?: UserFilterConfig;
  /** All active drilldowns; the ones reaching this widget AND after every configured filter. */
  drilldowns?: Drilldown[];
}

/** Map a WidgetConfig + resolved fields + dashboard filters into a ResolvedWidget. */
export function toResolvedWidget(
  widget: WidgetConfig,
  fields: FieldOption[],
  options: ToResolvedWidgetOptions = {}
): ResolvedWidget {
  const fieldByKey: Record<string, FieldOption> = {};
  for (const f of fields) fieldByKey[f.key] = f;

  const pathFor = (key: string) => {
    const baseKey = key.split('__')[0]!;
    const field = fieldByKey[baseKey];
    return field?.filterPath ?? field?.path ?? baseKey;
  };
  const ctx: FilterClauseContext = { pathFor, fieldByKey };

  const resolveMeasure = (spec: MeasureSpec): ResolvedMeasure => {
    const field = spec.fieldKey ? (fieldByKey[spec.fieldKey] ?? null) : null;
    return { id: spec.id, field, aggregation: spec.aggregation, label: measureLabel(spec, field) };
  };

  const shared = {
    modelType: getSearchRequestModelType(
      getModelTypeFromDataset(widget.dataset),
      widget.showIndividualApplicationsOnly ?? true
    ),
    filterClauses: widgetFilterClauses(
      widget,
      options.buildFilters,
      options.userFilters,
      options.drilldowns,
      ctx
    ),
    companyId: options.companyId,
    companyFilterField: getCompanyFilterField(widget.dataset),
    limit: widget.limit,
  };

  if (widget.widgetType === 'counter') {
    const measureField = widget.measure?.fieldKey
      ? (fieldByKey[widget.measure.fieldKey] ?? null)
      : null;
    return {
      ...shared,
      widgetType: 'counter',
      measureField,
      aggregation: widget.measure?.aggregation ?? 'count',
    };
  }

  if (widget.widgetType === 'pie') {
    return {
      ...shared,
      widgetType: 'pie',
      sliceField: widget.sliceDimension?.fieldKey
        ? fieldByKey[widget.sliceDimension.fieldKey]
        : undefined,
      sliceInterval: widget.sliceDimension?.dateInterval,
      measureField: widget.valueMeasure?.fieldKey
        ? (fieldByKey[widget.valueMeasure.fieldKey] ?? null)
        : null,
      aggregation: widget.valueMeasure?.aggregation ?? 'count',
      donut: widget.donut,
      colors: widget.format?.colors,
    };
  }

  const xField = widget.xDimension?.fieldKey ? fieldByKey[widget.xDimension.fieldKey] : undefined;
  const groupField = widget.groupDimension?.fieldKey
    ? fieldByKey[widget.groupDimension.fieldKey]
    : undefined;

  return {
    ...shared,
    widgetType: 'chart',
    xDimension: xField,
    xInterval: widget.xDimension?.dateInterval,
    groupDimension: groupField,
    bars: (widget.bars ?? []).map(resolveMeasure),
    lines: (widget.lines ?? []).map(resolveMeasure),
    barMode: widget.barMode ?? 'stacked',
    linesAxis: widget.linesAxis ?? 'secondary',
    colors: widget.format?.colors,
  };
}
